// APP-6/MIL-STD-2525C symbolkatalog + SIDC-bygging (ren, testbar logikk).
//
// 15-tegns SIDC: [S][affil][dim][P][funksjon×6][mod][echelon][land×2 → «--»][ob].
// Posisjon 12 = echelon (verifisert mot milsymbol). Alle funksjons-id-er i
// katalogen er verifisert til å gi et EKTE ikon (ikke bare ramme) — se
// app6.test.ts. Ingen falske symboler.

export type Affil = 'friend' | 'hostile' | 'neutral' | 'unknown';

export const AFFIL_CHAR: Record<Affil, string> = {
  friend: 'F',
  hostile: 'H',
  neutral: 'N',
  unknown: 'U',
};

export const AFFIL_LABEL: Record<Affil, string> = {
  friend: 'Venn',
  hostile: 'Fiende',
  neutral: 'Nøytral',
  unknown: 'Ukjent',
};

/** Echelon-koder (SIDC pos 12). Norske HV-nivåer. */
export const ECHELONS: Array<{ code: string; label: string }> = [
  { code: '-', label: '—' },
  { code: 'B', label: 'Lag' },
  { code: 'D', label: 'Tropp' },
  { code: 'E', label: 'Kompani' },
  { code: 'F', label: 'Bataljon' },
];

export interface UnitType {
  id: string;
  label: string;
  /** Funksjons-id (pos 5–10, opptil 6 tegn — padres med «-»). */
  func: string;
  /** Kampdimensjon: G bakke, A luft, S sjøoverflate. */
  dim: 'G' | 'A' | 'S';
}

/** Kuraterte enhetstyper for HV/totalforsvar. */
export const UNIT_TYPES: UnitType[] = [
  { id: 'inf', label: 'Infanteri', func: 'UCI', dim: 'G' },
  { id: 'mekinf', label: 'Mekanisert', func: 'UCIZ', dim: 'G' },
  { id: 'tank', label: 'Stridsvogn', func: 'UCA', dim: 'G' },
  { id: 'at', label: 'Panservern', func: 'UCAA', dim: 'G' },
  { id: 'recon', label: 'Oppklaring', func: 'UCR', dim: 'G' },
  { id: 'arty', label: 'Artilleri', func: 'UCF', dim: 'G' },
  { id: 'mortar', label: 'Bombekaster', func: 'UCFM', dim: 'G' },
  { id: 'ad', label: 'Luftvern', func: 'UCD', dim: 'G' },
  { id: 'eng', label: 'Ingeniør', func: 'UCE', dim: 'G' },
  { id: 'med', label: 'Sanitet', func: 'UCM', dim: 'G' },
  { id: 'sig', label: 'Samband', func: 'UUS', dim: 'G' },
  { id: 'supply', label: 'Forsyning', func: 'USS', dim: 'G' },
  { id: 'transport', label: 'Transport', func: 'UST', dim: 'G' },
  { id: 'hq', label: 'Stab/KO', func: 'UH', dim: 'G' },
  { id: 'mp', label: 'Militærpoliti', func: 'UUL', dim: 'G' },
  { id: 'uav', label: 'UAV', func: 'MFQ', dim: 'A' },
];

/** Bygger en 15-tegns SIDC fra funksjon + dimensjon + tilhørighet + echelon. */
export function buildSidc(func: string, dim: string, aff: Affil, echelon = '-'): string {
  const f = (func + '------').slice(0, 6);
  const ech = echelon || '-';
  const sidc = 'S' + AFFIL_CHAR[aff] + dim + 'P' + f + '-' + ech + '---';
  return sidc.slice(0, 15);
}

/** Omvendt oppslag av `AFFIL_CHAR` — SIDC-tegn (pos 2) tilbake til tilhørighet. */
export const AFFIL_FROM_CHAR: Record<string, Affil> = {
  F: 'friend',
  H: 'hostile',
  N: 'neutral',
  U: 'unknown',
};

export interface SidcDeler {
  aff: Affil;
  dim: UnitType['dim'];
  /** Funksjons-id uten hale-fyll («UCIZ», ikke «UCIZ--»). */
  func: string;
  echelon: string;
}

/**
 * Plukk en SIDC fra hverandre igjen.
 *
 * HVORFOR: symbolet var ENVEIS. `buildSidc` kunne lage en SIDC, men ingenting
 * kunne lese den tilbake — og derfor kunne en enhet som var plassert med feil
 * symbol ikke rettes. Enhetspaletten hadde ingen måte å åpne seg
 * FORHÅNDSUTFYLT med enhetens nåværende tilhørighet og echelon, så eneste
 * «reparasjon» var å slette enheten og tegne den på nytt — noe som i tillegg
 * kaster bort ORBAT-koblingen (id-en) og enhetens navn.
 *
 * Ukjente tegn faller til trygge standardverdier i stedet for å kaste: en SIDC
 * fra en annen operatør (eller en eldre bygg-versjon) skal kunne åpnes for
 * retting, ikke låse UI-et.
 */
export function parseSidc(sidc: string): SidcDeler {
  const s = (String(sidc) + '---------------').slice(0, 15);
  const dim = s[2];
  return {
    aff: AFFIL_FROM_CHAR[s[1]] ?? 'unknown',
    dim: dim === 'A' || dim === 'S' ? dim : 'G',
    func: s.slice(4, 10).replace(/-+$/, ''),
    echelon: ECHELONS.some((e) => e.code === s[11]) ? s[11] : '-',
  };
}

/**
 * Enhetstypen en SIDC svarer til, hvis den finnes i katalogen.
 * Brukes til å markere NÅVÆRENDE type i paletten ved retting. Returnerer
 * undefined for symboler utenfor katalogen (f.eks. BFT-markøren) — da skal
 * ingen rute stå som valgt, i stedet for at en tilfeldig rute lyver.
 */
export function typeForSidc(sidc: string): UnitType | undefined {
  const { func, dim } = parseSidc(sidc);
  return UNIT_TYPES.find((t) => t.func === func && t.dim === dim);
}
